// D137 (§7.2/§7.3) — the context mapper: held keys × what the ball is doing → the outgoing button word.
//
// The user's action set has three keys that legitimately mean two things (Space = pass / call for it,
// E = loft / hard tackle, Ctrl = block / shield) and one more meaning for Q and E when you are the
// keeper facing a penalty. Those splits are resolved HERE, in the client, because the sim must stay
// context-free: it receives plain BTN bits and has no idea who is human (invariant 3). Misprediction
// exposure is ≤ 1 RTT on a possession flip and fails safe — a wrong-context E sends LOFT, which is inert
// without a kick, or TACKLE, whose whiff is the ordinary whiff.
//
// Pure by construction: no DOM, no globals, and no clock of its own. The caller passes `nowMs`, which is
// what makes the double-tap detector independent of the frame cadence (and testable at 60 Hz and 20 Hz).
//
// Two rules earn their own machinery:
//   · CONTEXT IS LATCHED AT PRESS TIME. A possession flip mid-hold must not silently change what a held
//     key means — holding "call for it" through your own reception would otherwise turn into a PASS bit
//     and the sim's fresh-claim rule would one-touch the ball straight back out again.
//   · TAP AND DOUBLE-TAP ARE EXCLUSIVE. The single lob is therefore DEFERRED by DOUBLE_TAP_MS: emitting
//     it immediately would mean every double tap sent LOB and then LOB|LOFT, i.e. two lobs. The cost is
//     up to 250 ms of latency on the low lob, which is the price of the higher one existing at all.

import { BTN } from '@fafi/shared';
import { BINDING_BTN, CONTEXT_ACTIONS, type ActionBinding, type Bindings } from './bindings';

/** What the ball is doing, from the controlled player's point of view. */
export type BallContext = 'mine' | 'teammate' | 'opponent' | 'loose';

export interface MapContext {
  readonly ball: BallContext;
  /** The controlled player is the keeper defending a penalty — the ONE context where Q/E emit dive bits
   *  (PT4-P21). The gate is client-side on purpose: the SIM still honours a pressed dive bit anywhere in
   *  the box, so §18 T-ACT-009 keeps its meaning and bot keepers keep their `gkSkillMul` dive channel. */
  readonly defendingPenalty: boolean;
}

/** ms — a second loft-key press within this of the first release is the "higher lob" (PT4-P15). */
export const DOUBLE_TAP_MS = 250;

const KICK_VERBS = BTN.PASS | BTN.THROUGH | BTN.SHOOT;

/** Is `action` live in this context? Everything not listed is universal (§7.2's "universal" column).
 *  Exported since the touch wave: the verb panel is a VIEW of this rule (FAFI-Touch-Plan §2.5) — its
 *  layout tables are property-tested against it (T-TCH-003), so panel and mapper cannot disagree. */
export function activeInContext(action: ActionBinding, ctx: MapContext): boolean {
  switch (action) {
    case 'gkDiveL':
    case 'gkDiveR':
      return ctx.defendingPenalty;
    case 'call':
      return ctx.ball === 'teammate';
    case 'passDirect':
      return ctx.ball !== 'teammate';
    case 'tackleHard':
      return ctx.ball === 'opponent' && !ctx.defendingPenalty;
    case 'loft':
      return ctx.ball !== 'opponent' && !ctx.defendingPenalty;
    case 'shield':
      return ctx.ball === 'mine';
    case 'block':
      return ctx.ball !== 'mine';
    default:
      return true;
  }
}

export class ContextMapper {
  /** code → the bits it resolved to at press time (the latch). */
  private readonly latched = new Map<string, number>();
  /** ms the loft key went down, or null when it is up. */
  private loftDownAt: number | null = null;
  /** this hold has already produced its outcome (a modifier kick, or the high lob of a double tap) ⇒ its
   *  release must not also schedule the low lob. */
  private loftSpent = false;
  /** ms at which a deferred single lob should go out, or null. */
  private singleLobAt: number | null = null;
  private prevKickVerbs = 0;

  constructor(private readonly doubleTapMs: number = DOUBLE_TAP_MS) {}

  reset(): void {
    this.latched.clear();
    this.loftDownAt = null;
    this.loftSpent = false;
    this.singleLobAt = null;
    this.prevKickVerbs = 0;
  }

  /** Resolve every action bound to `code` that is live in `ctx`, and OR their bits. */
  private bitsFor(code: string, ctx: MapContext, bindings: Bindings): number {
    if (code === '') return 0;
    let bits = 0;
    for (const action of CONTEXT_ACTIONS) {
      if (bindings[action] === code && activeInContext(action, ctx)) bits |= BINDING_BTN[action];
    }
    return bits;
  }

  /** The outgoing button word for this frame. */
  map(held: ReadonlySet<string>, ctx: MapContext, bindings: Bindings, nowMs: number): number {
    for (const code of [...this.latched.keys()]) if (!held.has(code)) this.latched.delete(code);
    for (const code of held) if (!this.latched.has(code)) this.latched.set(code, this.bitsFor(code, ctx, bindings));

    let out = 0;
    for (const bits of this.latched.values()) out |= bits;

    // --- the lob, off the loft key only when it actually resolved to LOFT this hold ---
    const loftCode = bindings.loft;
    const loftIsLoft = loftCode !== '' && ((this.latched.get(loftCode) ?? 0) & BTN.LOFT) !== 0;
    const kickPressed = (out & KICK_VERBS & ~this.prevKickVerbs) !== 0;
    this.prevKickVerbs = out & KICK_VERBS;

    if (loftIsLoft && this.loftDownAt === null) {
      // press edge. A second press inside the window is the HIGH lob, resolved before the deferred low
      // one below — so a double tap is never preceded by a single (the exclusivity rule).
      this.loftDownAt = nowMs;
      this.loftSpent = false;
      if (this.singleLobAt !== null && nowMs <= this.singleLobAt) {
        this.singleLobAt = null;
        this.loftSpent = true; // …and this hold is DONE: its own release must not queue a third lob
        return out | BTN.LOB | BTN.LOFT;
      }
    } else if (!loftIsLoft && this.loftDownAt !== null) {
      // release edge: a short press with no kick fired during it is a tap ⇒ schedule the low lob
      if (!this.loftSpent && nowMs - this.loftDownAt <= this.doubleTapMs) this.singleLobAt = nowMs + this.doubleTapMs;
      this.loftDownAt = null;
    } else if (loftIsLoft && kickPressed) {
      this.loftSpent = true; // E + a kick verb is the modifier, which is not a lob
    }

    if (this.singleLobAt !== null && nowMs >= this.singleLobAt) {
      this.singleLobAt = null;
      return out | BTN.LOB;
    }
    return out;
  }
}
